import React, { useState, useEffect } from 'react';
import Select, { type StylesConfig } from 'react-select';
import type { SingleValue } from 'react-select';
import { Modal } from '@/components/utils/Modal';
import { apiService } from '@/services/api';
import { useToast } from '@components/ui/Toast';

interface ProjectOption {
  value: number;
  label: string;
}

interface ChangeResult {
  ConversationId: number;
  ConfigValueIcon: string;
  Status: boolean;
  Title: string;
  PrevProjectName: string;
  NewProjectName: string;
  ErrorMessage?: string;
}

interface ConversationRef {
  ConversationId: number;
  ConfigValueId?: number;
}

interface ChangeProjectModalProps {
  open: boolean;
  onClose: () => void;
  conversations: ConversationRef[];
  onProjectChanged?: (
    results: Array<{ ConversationId: number; ProjectId: number; ProjectName: string }>
  ) => void;
}

const getComboBoxSelectStyles = (isDark: boolean): StylesConfig<ProjectOption, false> => ({
  control: (base, state) => ({
    ...base,
    minHeight: '32px',
    height: '32px',
    borderRadius: '2px',
    boxShadow: 'none',
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    backgroundColor: isDark ? '#3a3c3c' : base.backgroundColor,
    borderColor: state.isFocused ? (isDark ? '#62accb' : '#66afe9') : isDark ? '#4a4c4c' : '#ccc',
    '&:hover': { borderColor: isDark ? '#62accb' : '#66afe9' },
  }),
  valueContainer: base => ({
    ...base,
    height: '32px',
    padding: '0 8px',
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    lineHeight: 'normal',
    color: isDark ? '#a9a093' : base.color,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: base => ({
    ...base,
    padding: '4px 6px',
    color: isDark ? '#6e6860' : base.color,
  }),
  clearIndicator: base => ({ ...base, padding: '4px', color: isDark ? '#6e6860' : base.color }),
  placeholder: base => ({
    ...base,
    fontSize: '12px',
    lineHeight: 'normal',
    color: isDark ? '#6e6860' : '#aaa',
  }),
  singleValue: base => ({
    ...base,
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    lineHeight: 'normal',
    color: isDark ? '#a9a093' : 'rgb(158, 149, 135)',
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 1000200,
  }),
  menu: base => ({
    ...base,
    marginTop: 0,
    borderRadius: '2px',
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    backgroundColor: isDark ? '#333535' : base.backgroundColor,
  }),
  menuList: base => ({
    ...base,
    padding: 0,
    maxHeight: '200px',
  }),
  option: (_base, state) => ({
    display: 'block',
    boxSizing: 'border-box' as const,
    width: '100%',
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    lineHeight: '1.4',
    padding: '6px 10px',
    backgroundColor: state.isFocused ? '#62accb' : isDark ? '#333535' : '#fff',
    color: state.isFocused ? '#fff' : isDark ? '#a9a093' : '#333',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
});

const ChangeProjectModal: React.FC<ChangeProjectModalProps> = ({
  open,
  onClose,
  conversations,
  onProjectChanged,
}) =>
{
  const isDark = document.documentElement.classList.contains('dark');
  const comboBoxSelectStyles = getComboBoxSelectStyles(isDark);
  const toast = useToast();
  const [options, setOptions] = useState<ProjectOption[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
  const [viewType, setViewType] = useState<1 | 2>(1);
  const [summaryData, setSummaryData] = useState<ChangeResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() =>
  {
    if (!open) return;
    setViewType(1);
    setSelectedProject(null);
    setSummaryData([]);

    const configValueId = conversations[0]?.ConfigValueId ?? 9;
    apiService
      .post<{ Projects: { ProjectID: number; Name: string }[] }>('Project/GetSideMenuProjects', {
        ConfigValueId: configValueId,
        Filters: [],
        IsFromProject: false,
        ProjectName: '',
        GroupByLayerId: 0,
        GroupByDetailId: 0,
        GroupByValueField: '',
      })
      .then(data =>
      {
        setOptions((data?.Projects ?? []).map(p => ({ value: p.ProjectID, label: p.Name })));
      })
      .catch(console.error);
  }, [open]);

  const conversationIds = conversations.map(c => c.ConversationId).join(',');

  const handleNext = async () =>
  {
    if (!selectedProject) return;
    setIsLoading(true);
    try
    {
      const result = await apiService.post<ChangeResult[]>(
        'Conversation/ChangeConversationsProject',
        {
          ConversationsIds: conversationIds,
          configValueName: 'Project',
          IsPreview: 1,
          ProjectId: selectedProject.value,
        },
      );
      setSummaryData(result ?? []);
      setViewType(2);
    }
    catch (e)
    {
      console.error(e);
    }
    finally
    {
      setIsLoading(false);
    }
  };

  const handleApply = async () =>
  {
    if (!selectedProject) return;
    setIsLoading(true);
    // Use IDs captured in the Preview step (summaryData) rather than the current
    // `conversationIds` prop, which could shrink if the parent's selection is cleared
    // while the modal is open (React portal events bubble to DashboardGroups).
    const stableIds = summaryData.length > 0
      ? summaryData.map(r => r.ConversationId).join(',')
      : conversationIds;
    try
    {
      await apiService.post('Conversation/ChangeConversationsProject', {
        ConversationsIds: stableIds,
        configValueName: 'Project',
        IsPreview: 0,
        ProjectId: selectedProject.value,
      });
      if (onProjectChanged)
      {
        const changed = summaryData
          .filter(item => item.Status)
          .map(item => ({
            ConversationId: item.ConversationId,
            ProjectId: selectedProject.value,
            ProjectName: selectedProject.label,
          }));
        onProjectChanged(changed);
      }

      // Same wording as the multi-edit modal: report how many cards actually changed rather
      // than a generic "dashboard changed" message.
      const changedCount = summaryData.filter(item => item.Status).length;
      toast.success(`${changedCount} item${changedCount !== 1 ? 's' : ''} changed`);

      onClose();
    }
    catch (e)
    {
      console.error(e);
    }
    finally
    {
      setIsLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Change project to" preventCloseClickOut>
      {viewType === 1 && (
        <div className="w-96">
          <div className="gap-4">
            <div className="flex">
              <div className="w-24 flex-none text-sm align-middle leading-[45px]">Project:</div>
              <div className="flex-1">
                <Select<ProjectOption>
                  options={options}
                  value={selectedProject}
                  onChange={(opt: SingleValue<ProjectOption>) => setSelectedProject(opt ?? null)}
                  isSearchable={true}
                  isClearable={true}
                  className="w-full"
                  styles={comboBoxSelectStyles}
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                  placeholder="Select..."
                />
              </div>
            </div>
          </div>
          <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
            <button
              type="button"
              disabled={!selectedProject || isLoading}
              onClick={handleNext}
              className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1 disabled:opacity-50"
            >
              {isLoading ? 'Loading...' : 'Next'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {viewType === 2 && (
        <div className="w-96">
          <div
            className="changeCards"
            style={{
              height: '300px',
              display: 'block',
              background: isDark ? '#333535' : 'rgb(255,255,255)',
              overflow: 'auto',
              marginBottom: 12,
            }}
          >
            <span><b>Summary:</b></span>

            {/* Success items */}
            <div>
              {summaryData.filter(item => item.Status).map((item, i) => (
                <div key={i}>
                  <div style={{ fontSize: '12px', color: isDark ? '#a9a093' : '#414042', margin: '0.5em 0' }}>
                    <div>
                      <div style={{ display: 'inline-block', verticalAlign: 'middle', paddingRight: '4px' }}>
                        <div className={item.ConfigValueIcon}></div>
                      </div>
                      <div style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                        <div className="detail-update-description">{item.Title}</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: '10px', color: isDark ? '#6e6860' : '#a5a7ac' }}>Project</div>
                  <div style={{ display: 'inline-block', verticalAlign: 'top', paddingRight: '12px', paddingBottom: '8px' }}>
                    <del style={{ fontSize: '12px', textDecoration: 'line-through', color: isDark ? '#4a4c4c' : '#d0d0d0', marginRight: '4px', display: 'inline-block' }}>
                      {!item.PrevProjectName ? 'Not Set' : item.PrevProjectName}
                    </del>
                    <div style={{ color: isDark ? '#a9a093' : '#414042', fontSize: '12px', wordBreak: 'break-all', display: 'inline-block' }}>
                      {!item.NewProjectName ? 'Not Set' : item.NewProjectName}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Error items */}
            <div>
              {summaryData.filter(item => !item.Status).map((item, i) => (
                <div key={i}>
                  <div style={{ fontSize: '12px', color: isDark ? '#a9a093' : '#414042', margin: '0.5em 0' }}>
                    <div>
                      <div style={{ display: 'inline-block', verticalAlign: 'middle', paddingRight: '4px' }}>
                        <div className={item.ConfigValueIcon}></div>
                      </div>
                      <div style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                        <div className="detail-update-description">{item.Title}</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'inline-block', verticalAlign: 'top', paddingRight: '12px' }}>
                    <div style={{ color: isDark ? '#a9a093' : '#414042', fontSize: '12px', wordBreak: 'break-all', display: 'inline-block' }}>
                      {item.ErrorMessage}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {summaryData.some(item => item.Status) ? (
            <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
              <button
                type="button"
                disabled={isLoading}
                onClick={handleApply}
                className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1 disabled:opacity-50"
              >
                {isLoading ? 'Applying...' : 'Apply'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
              <button
                type="button"
                onClick={onClose}
                className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ChangeProjectModal;
